const toMs = (d) => (d ? new Date(d).getTime() : 0);

export const linkedIds = (event) => (event?.configLinks || []).map((l) => l.configFileId).filter(Boolean);

// How an event's dates publish (its runMode): REVIEW, the default (also when unset), waits for a person to publish each
// date; DEFAULTS publishes a placement's next DEFAULTS_AHEAD dates itself, on the event as it stands.
export const DEFAULTS_AHEAD = 5;

export const publishesItself = (event) => event?.runMode === 'DEFAULTS';

// What stops an event publishing itself, worded and ordered as the server's LiveEventContractRules.defaultsBlockers.
// The How dates publish panel asks the server; this copy serves the playground, the Placements line and the Flywheel board.
export const defaultsBlockers = (event) => [
    !linkedIds(event).length && 'It links no config.',
    !(event?.durationSeconds > 0) && 'It has no duration, so a date would never end.',
    !event?.target && !(event?.guardrails || []).length
        && 'It has no target or guardrail, so a date that shipped itself could not be judged.',
].filter(Boolean);

// Who publishes the event's dates: 'person', 'self', or 'stalled' when it is set to publish itself but can't yet.
export const publishingOf = (event) => {
    if (!publishesItself(event)) return 'person';
    return defaultsBlockers(event).length ? 'stalled' : 'self';
};

export const toTarget = (t) => (t?.metricId ? {
    metric: t.metricId,
    direction: t.direction || 'UP',
    mode: t.mode || 'PERCENT',
    percent: t.percent ?? null,
    value: t.value ?? null,
    holdTolerancePct: t.holdTolerancePct ?? null,
} : null);

// A "reach a level" goal is judged by the level reached; the % change only tells a near miss from a miss.
export const isLevelGoal = (t) => t?.mode === 'ABSOLUTE' && t.direction !== 'HOLD';

export const fromTarget = (t) => (t?.metric ? {
    metricId: t.metric, direction: t.direction, mode: t.mode, percent: t.percent, value: t.value, holdTolerancePct: t.holdTolerancePct,
} : {});

export const toGuardrails = (list) => (list || []).map((g) => ({
    id: g.id, metricKey: g.metricId, direction: g.direction || 'MUST_NOT_FALL', tolerancePercent: g.tolerancePct ?? null,
}));

export const fromGuardrail = (g) => ({ metricId: g.metricKey, direction: g.direction, tolerancePct: g.tolerancePercent ?? null });

export const toMetrics = (list) => (list || [])
    .filter((m) => m.metricId)
    .map((m) => ({ id: m.metricId, label: m.metricName || m.metricId }))
    .sort((a, b) => a.label.localeCompare(b.label));

const shippedOrSkipped = (e) => e.kind === 'DATE' && (e.status === 'SKIPPED' || (!!e.publishedAt && e.status !== 'CANCELLED'));

// Dates that didn't run. They stay in the record so a gap says why.
const NOT_RUN = { SKIPPED: 'Skipped', MISSED: 'Missed', CANCELLED: 'Cancelled' };
const dueDate = (e) => e.kind === 'DATE' && (!!NOT_RUN[e.status] || !!e.publishedAt);

export const toRecordRows = (history, targetLabel, labelOf) => (history || []).filter(dueDate).map((e) => {
    const o = e.outcome;
    // The goal the result was judged against, as it stood then: a later change to the goal doesn't re-score it.
    const goal = toTarget(o?.targetSnapshot);
    return {
        instanceId: e.instanceId,
        runId: e.runId || null,
        occStartMs: toMs(e.at),
        windowEndMs: toMs(e.windowEndsAt),
        state: NOT_RUN[e.status] || 'Ended',
        shipped: (e.shipped || []).filter((s) => !s.removed).map((s) => ({ path: s.path, value: s.value })),
        targetLabel: goal ? labelOf(goal.metric) : targetLabel,
        goal,
        targetMetricPct: o?.deltaPct ?? null,
        actual: o?.actual ?? null,
        baseline: o?.baseline ?? null,
        guardrails: (o?.guardrails || []).filter((g) => g.deltaPct != null).map((g) => ({
            metricKey: g.guardrail?.metricId, label: labelOf(g.guardrail?.metricId), pct: g.deltaPct, broken: !!g.broken,
        })),
        verdict: o?.verdict && o.verdict !== 'NOT_SCORED' ? o.verdict : 'NOT_MEASURED',
        measured: !!o,
        source: o?.source || null,
        inconclusiveReason: o?.inconclusiveReason || null,
        inconclusiveDetail: o?.inconclusiveDetail || null,
        corrections: (o?.revisions || []).length,
        note: o?.note || null,
        skipReason: e.skipReason || null,
        auto: false,
    };
});

export const runsDone = (history) => (history || []).filter(shippedOrSkipped).length;
